import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import apiClient from "../services/apiClient";

// Create base store configuration for comments
const commentsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/comments",
  name: "CommentsStoreRefactor",
  objectName: "Opmerking",
  enableCache: false,
  actionCableChannel: "CommentsChannel", // Class name
  infiniteLoading: false,
});

export const useCommentsStoreRefactor = defineStore("commentsRefactor", {
  ...commentsBaseStore,

  state: () => ({
    ...commentsBaseStore.state(),
    // Normalized storage for efficient lookups
    commentsById: {},
  }),

  getters: {
    ...commentsBaseStore.getters,

    /**
     * Get all comments as array (for backward compatibility)
     */
    comments: (state) => {
      return Object.values(state.commentsById).sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at),
      );
    },

    /**
     * Get comments filtered by commentable type and ID
     */
    getCommentsByTypeAndId: (state) => (type, id) => {
      return Object.values(state.commentsById)
        .filter(
          (comment) =>
            comment.commentable_type === type &&
            Number(comment.commentable_id) === Number(id),
        )
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    },

    /**
     * Get comment by ID (fast O(1) lookup)
     */
    getCommentById: (state) => (id) => {
      return state.commentsById[id] || null;
    },

    /**
     * Get comments count for a specific entity
     */
    getCommentsCountByTypeAndId: (state) => (type, id) => {
      return Object.values(state.commentsById).filter(
        (comment) =>
          comment.commentable_type === type &&
          Number(comment.commentable_id) === Number(id),
      ).length;
    },
  },

  actions: {
    ...commentsBaseStore.actions,

    /**
     * Override fetchAll since comments don't have a general endpoint
     */
    async fetchAll() {
      console.log(
        `${this.name}: fetchAll called - comments are entity-specific, skipping`,
      );
      return [];
    },

    /**
     * Fetch comments for a specific entity
     * @param {String} type - Entity type (e.g., 'Report', 'DayReport')
     * @param {Number} id - Entity ID
     */
    async fetchCommentsByTypeAndId(type, id) {
      try {
        this.loading = true;
        this.error = null;

        // Convert type to plural snake_case for API endpoint
        const pluralType =
          type
            .replace(/([A-Z])/g, "_$1")
            .toLowerCase()
            .replace(/^_/, "") + "s";

        const endpoint = `/api/v1/${pluralType}/${id}/comments`;

        const comments = await apiClient.get(
          endpoint,
          {},
          { showErrorAlert: false },
        );

        console.log(
          `${this.name}: Fetched ${comments.length} comments for ${type} ${id}`,
        );

        // Clear existing comments for this entity
        this._clearEntityComments(type, id);

        // Use base store's _mergeData to add comments
        this._mergeData(comments || []);

        return comments || [];
      } catch (error) {
        this.error =
          error.message ||
          "Er is een fout opgetreden bij het laden van reacties";
        console.error(
          `${this.name}: Error fetching comments for ${type} ${id}:`,
          error,
        );
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // CRUD operations now fully inherited with auto-generated success messages

    /**
     * Override base store's _upsertItem to maintain normalized storage
     * @private
     */
    _upsertItem(item, prepend = false) {
      // Call base store method first
      commentsBaseStore.actions._upsertItem.call(this, item, prepend);

      // Update normalized storage
      if (item?.id) {
        this.commentsById[item.id] = { ...item };
        console.log(
          `${this.name}: Updated normalized storage for comment ${item.id}`,
        );
      }
    },

    /**
     * Override base store's _removeItem to maintain normalized storage
     * @private
     */
    _removeItem(id) {
      // Call base store method first
      commentsBaseStore.actions._removeItem.call(this, id);

      // Update normalized storage
      if (this.commentsById[id]) {
        delete this.commentsById[id];
        console.log(
          `${this.name}: Removed comment ${id} from normalized storage`,
        );
      }
    },

    /**
     * Override base store's _mergeData to maintain normalized storage
     * @private
     */
    _mergeData(newData) {
      // Call base store method first
      commentsBaseStore.actions._mergeData.call(this, newData);

      // Update normalized storage
      newData.forEach((item) => {
        if (item?.id) {
          this.commentsById[item.id] = { ...item };
        }
      });
    },

    /**
     * Clear all comments for a specific entity
     * @private
     */
    _clearEntityComments(type, id) {
      console.log(`${this.name}: Clearing existing comments for ${type} ${id}`);

      // Find comments to remove
      const commentsToRemove = Object.values(this.commentsById).filter(
        (comment) =>
          comment.commentable_type === type &&
          Number(comment.commentable_id) === Number(id),
      );

      // Remove from both storages
      commentsToRemove.forEach((comment) => {
        delete this.commentsById[comment.id];
      });

      // Remove from data array
      this.data = this.data.filter(
        (comment) =>
          !(
            comment.commentable_type === type &&
            Number(comment.commentable_id) === Number(id)
          ),
      );
    },

    /**
     * Clear all comments and reset state
     */
    clearComments() {
      this.commentsById = {};
      this.clear(); // Use base store's clear method
    },
  },
});
